import { useContext, useEffect, useState } from "react";
import { Form, Input, Button, Select, Modal } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getExecutiveEmployeeList,
  updateLeadDetails,
} from "../../actionCreator/leadListingActionCreater";
import { useAlert } from "../../context/AlertProvider";
import { getUserInfo } from "../../utils/utils";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { AuthContext } from "../../context/AuthContext";

const UpdateLeadDetails = ({ lead }) => {
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    if (isLeadEdit) {
      setIsModalOpen(true);
    } else {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to update lead."
      );
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <EditOutlined className="heading14px" onClick={showModal} />
      <Modal
        title={
          <div
            className="heading24px text-color-1C1C1C roboto-medium"
            style={{ padding: "20px 24px 10px" }}
          >
            Lead Details
          </div>
        }
        className="delete-popup"
        open={isModalOpen}
        centered
        footer={null}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <LeadForm lead={lead} handleCancel={handleCancel} />
      </Modal>
    </>
  );
};

export default UpdateLeadDetails;

const LeadForm = ({ lead, handleCancel }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [form] = Form.useForm();
  const { id: userId } = getUserInfo();
  const [error, setError] = useState();
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList
  );

  useEffect(() => {
    form.setFieldsValue({
      ...lead,
      status: {
        label: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
        value: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
      },
      phoneNo: formatAndExtractCountryCode(lead?.phoneNo?.split("/")?.[0])
        ?.numberWithoutCountryCode,
      prefix: formatAndExtractCountryCode(lead?.phoneNo?.split("/")?.[0])
        ?.countryLabel,
    });
  }, [lead, form, leadStatusListing]);

  const onFinish = async (values) => {
    const res = await dispatch(
      updateLeadDetails({
        ...values,
        fullname: values?.firstname + " " + values?.lastname,
        status: leadStatusListing?.find((d) => d.text === values?.status)?.id,
        id: lead?.id,
        userId,
        phoneNo:
          "+" +
          countries?.find((d) => d?.label === values?.prefix)?.code +
          " " +
          values?.phoneNo,
      })
    );
    if (res.status === 200) {
      showAlert(
        "success",
        `${res?.data?.firstname} ${res?.data?.lastname} Lead updated successfully!`
      );

      dispatch(
        getExecutiveEmployeeList(
          { selectedPageLayout: 2 },
          { paginationValue: { start: 0, end: 10 } },
          true
        )
      );

      handleCancel();
      form.resetFields();
    } else {
      if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          "You do not have permission to update lead."
        );
        handleCancel();
        form.resetFields();
        setError();
      } else {
        showAlert("error", res.response?.data?.title);
        setError(res.response?.data?.title);
      }
    }
  };

  return (
    <div style={{ padding: "20px 24px 20px 24px" }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...lead,
          status: {
            label: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
            value: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
          },
        }}
        onFinish={onFinish}
        style={{ maxWidth: 600, margin: "0 auto" }}
      >
        {error && <p className="text-error text-center pb-2">{error}</p>}
        <Form.Item
          name="firstname"
          label="First Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter first name" }]}
        >
          <Input className="common-input" />
        </Form.Item>
        <Form.Item
          name="lastname"
          label="Last Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter last name" }]}
        >
          <Input className="common-input" />
        </Form.Item>

        <Form.Item
          name="phoneNo"
          label="Phone"
          rules={[{ required: true, message: "Please enter phone number." }]}
        >
          <Input
            addonBefore={prefixSelector()}
            className="common-input"
            maxLength={20}
            onChange={(e) => handleMobileChange(e.target.value, form)}
          />
        </Form.Item>

        <Form.Item
          name="companyName"
          label="Company Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter company name." }]}
        >
          <PlaceAutocomplete
            onPlaceSelect={(v) => {
              form.setFieldsValue({
                companyName: v?.name,
                website: v?.website,
              });
            }}
          />
        </Form.Item>
        <Form.Item name="website" label="Website">
          <Input className="common-input" />
        </Form.Item>
        <Form.Item
          name="status"
          label="Status"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please select status." }]}
        >
          <Select
            options={leadStatusListing?.map((d) => ({
              label: d.text,
              value: d.text,
            }))}
            onChange={(value) => {
              form.setFieldsValue({
                status: value,
              });
            }}
          />
        </Form.Item>

        <div
          className="mt40 d-flex justify-content-end align-items-center"
          style={{ gap: 20 }}
        >
          <Button
            className="secondary-btn"
            onClick={() => {
              handleCancel();
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button type="primary" className="theme-btn" htmlType="submit">
            Update
          </Button>
        </div>
      </Form>
    </div>
  );
};
